import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QuestionContent from './QuestionContent';
import styles from './MarkdownEditor.module.css';

const CODE_LANGS = [
    { label: 'Java', lang: 'java' },
    { label: 'Python', lang: 'python' },
    { label: 'JS', lang: 'javascript' },
    { label: 'SQL', lang: 'sql' },
    { label: 'C++', lang: 'cpp' },
];

function insertMarkdown(taRef, value, onChange, before, after = '', placeholder = '') {
    const ta = taRef.current;
    if (!ta) {
        onChange(value + before + placeholder + after);
        return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selection = value.substring(start, end);
    const inserted = before + (selection || placeholder) + after;
    const newValue = value.substring(0, start) + inserted + value.substring(end);
    onChange(newValue);

    requestAnimationFrame(() => {
        ta.focus();
        if (selection) {
            ta.setSelectionRange(start + before.length, start + before.length + selection.length);
        } else {
            ta.setSelectionRange(start + before.length, start + before.length + placeholder.length);
        }
    });
}

const MarkdownEditor = ({
    value,
    onChange,
    placeholder,
    showPreview = false,
    showLangShortcuts = false,
    textareaClassName,
    minRows = 4,
}) => {
    const { t } = useTranslation();
    const [preview, setPreview] = useState(false);
    const taRef = useRef(null);

    const insert = (before, after = '', ph = '') =>
        insertMarkdown(taRef, value, onChange, before, after, ph);

    const insertCodeBlock = (lang) =>
        insert('```' + lang + '\n', '\n```', '// code here');

    return (
        <div className={styles.wrapper}>
            <div className={styles.toolbar}>
                <div className={styles.toolbarLeft}>
                    {showLangShortcuts && CODE_LANGS.map(({ label, lang }) => (
                        <button
                            key={lang}
                            type="button"
                            className={styles.langBtn}
                            onClick={() => insertCodeBlock(lang)}
                            title={t("common.insertLangCodeBlock", { lang: label })}
                        >
                            {label}
                        </button>
                    ))}
                    <button
                        type="button"
                        className={styles.toolBtn}
                        onClick={() => insert('```\n', '\n```', '// code here')}
                        title={t("common.insertCodeBlock")}
                    >
                        {'</>'}
                    </button>
                    <button
                        type="button"
                        className={styles.toolBtn}
                        onClick={() => insert('`', '`', 'kod')}
                        title={t("common.insertInlineCode")}
                    >
                        {'`···`'}
                    </button>
                    <div className={styles.separator} />
                    <button
                        type="button"
                        className={styles.toolBtn}
                        onClick={() => insert('$$\n', '\n$$', 'E = mc^2')}
                        title={t("common.insertMathBlock")}
                    >
                        ∑ block
                    </button>
                    <button
                        type="button"
                        className={styles.toolBtn}
                        onClick={() => insert('$', '$', 'x^2')}
                        title={t("common.insertInlineMath")}
                    >
                        $ inline
                    </button>
                </div>

                {showPreview && (
                    <div className={styles.tabs}>
                        <button
                            type="button"
                            className={`${styles.tab} ${!preview ? styles.tabActive : ''}`}
                            onClick={() => setPreview(false)}
                        >
                            {t("common.write")}
                        </button>
                        <button
                            type="button"
                            className={`${styles.tab} ${preview ? styles.tabActive : ''}`}
                            onClick={() => setPreview(true)}
                        >
                            {t("common.preview")}
                        </button>
                    </div>
                )}
            </div>

            {preview ? (
                <div className={styles.preview}>
                    {value ? <QuestionContent text={value} /> : (
                        <span className={styles.previewEmpty}>{t("common.noContentEntered")}</span>
                    )}
                </div>
            ) : (
                <textarea
                    ref={taRef}
                    className={`${styles.textarea} ${textareaClassName || ''}`}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    rows={minRows}
                />
            )}
        </div>
    );
};

export default MarkdownEditor;
